// src/pages/settings/SettingsPage.tsx - User settings page

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { 
  fetchSettings,
  updateSettings
} from '../../store/slices/settingsSlice';
import { clearProfile } from '../../store/slices/profileSlice';
import { clearEntries } from '../../store/slices/journalSlice';
import { clearRelapseData } from '../../store/slices/relapseSlice';
import { logoutUser } from '../../store/slices/authSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  
  const { settings, isLoading, error } = useSelector((state: RootState) => state.settings);
  
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [notifications, setNotifications] = useState(true);
  const [defaultReflectionDepth, setDefaultReflectionDepth] = useState(2);
  const [shareData, setShareData] = useState(false);
  const [allowAnalytics, setAllowAnalytics] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  
  useEffect(() => {
    if (currentUser) {
      dispatch(fetchSettings(currentUser.uid));
    }
  }, [currentUser, dispatch]);
  
  useEffect(() => {
    if (settings) {
      setTheme(settings.theme);
      setNotifications(settings.notifications);
      setDefaultReflectionDepth(settings.defaultReflectionDepth);
      setShareData(settings.privacySettings.shareData);
      setAllowAnalytics(settings.privacySettings.allowAnalytics);
    }
  }, [settings]);
  
  const handleSaveSettings = async () => {
    if (!currentUser) return;
    
    const updatedSettings = {
      theme,
      notifications,
      defaultReflectionDepth,
      privacySettings: {
        shareData,
        allowAnalytics
      }
    };
    
    await dispatch(updateSettings({
      userId: currentUser.uid,
      updates: updatedSettings
    }));
  };
  
  const handleResetData = async () => {
    if (!currentUser) return;
    
    // In a real app, this would delete data from Firebase
    // For this MVP, we'll just clear the Redux store
    dispatch(clearProfile());
    dispatch(clearEntries());
    dispatch(clearRelapseData());
    
    setResetDialogOpen(false);
    navigate('/onboarding');
  };
  
  const handleLogout = async () => {
    await dispatch(logoutUser());
    setLogoutDialogOpen(false);
    navigate('/login');
  };
  
  if (isLoading && !settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          Settings
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Appearance
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Theme
          </Typography>
          
          <ToggleButtonGroup
            value={theme}
            exclusive
            onChange={(e, newTheme) => {
              if (newTheme) setTheme(newTheme);
            }}
            aria-label="theme"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="light" aria-label="light theme">
              Light
            </ToggleButton>
            <ToggleButton value="dark" aria-label="dark theme">
              Dark
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>
          Notifications
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={notifications}
              onChange={(e) => setNotifications(e.target.checked)}
            />
          }
          label="Enable notifications"
        />
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>
          Reflection Settings
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Default Reflection Depth
          </Typography>
          
          <ToggleButtonGroup
            value={defaultReflectionDepth}
            exclusive
            onChange={(e, newDepth) => {
              if (newDepth) setDefaultReflectionDepth(newDepth);
            }}
            aria-label="reflection depth"
          >
            <ToggleButton value={1} aria-label="level 1">
              Level 1
            </ToggleButton>
            <ToggleButton value={2} aria-label="level 2">
              Level 2
            </ToggleButton>
            <ToggleButton value={3} aria-label="level 3">
              Level 3
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {defaultReflectionDepth === 1 && "Surface observations and gentle encouragement"}
            {defaultReflectionDepth === 2 && "Moderate analysis with pattern recognition"}
            {defaultReflectionDepth === 3 && "Deep insights and challenging questions"}
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>
          Privacy
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText
              primary="Share anonymous data to improve the app"
              secondary="No personal information will be shared"
            />
            <Switch
              edge="end"
              checked={shareData}
              onChange={(e) => setShareData(e.target.checked)}
            />
          </ListItem>
          
          <ListItem>
            <ListItemText
              primary="Allow analytics"
              secondary="Help us understand how the app is used"
            />
            <Switch
              edge="end"
              checked={allowAnalytics}
              onChange={(e) => setAllowAnalytics(e.target.checked)}
            />
          </ListItem>
        </List>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Save Settings'}
          </Button>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText
              primary="Reset All Data"
              secondary="This will delete all your journal entries, reflections, and progress"
            />
            <Button
              variant="outlined"
              color="error"
              onClick={() => setResetDialogOpen(true)}
            >
              Reset
            </Button>
          </ListItem>
          
          <Divider />
          
          <ListItem>
            <ListItemText
              primary="Logout"
              secondary="Sign out of your account"
            />
            <Button
              variant="outlined"
              onClick={() => setLogoutDialogOpen(true)}
            >
              Logout
            </Button>
          </ListItem>
        </List>
      </Paper>
      
      {/* Reset Data Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
      >
        <DialogTitle>Reset All Data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all your journal entries, reflections, relapse tracking data, and progress. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetData} color="error">
            Reset All Data
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
      >
        <DialogTitle>Logout?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to sign out of your account?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLogout}>Logout</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;
